import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from "react-oidc-context";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Box, Paper, Typography, Button, CircularProgress, Alert,
    Stepper, Step, StepLabel, Divider, Container, TextField, Chip, IconButton, Stack,
    ToggleButton, ToggleButtonGroup, Collapse, Tooltip, Fade
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import UndoIcon from '@mui/icons-material/Undo';
import CloseIcon from '@mui/icons-material/Close';
import config from './config.json';

// --- TYPES ---
interface ProposalItem {
    ProposalId: string;
    SortKey: string;
    content: string;
    type?: string;
    is_user_edited?: boolean;
    related_question?: string;
}

interface WorkbenchResponse {
    phase: string;
    items: ProposalItem[];
    message?: string;
}

// --- LOCAL API SERVICE ---
const getApiAuthHeaders = (auth: any) => {
    const userApiKey = auth.user?.profile?.['custom:api_key'] as string;
    const id_token = auth.user?.id_token;
    if (!userApiKey || !id_token) {
        throw new Error('Authentication credentials not available. Please log in again.');
    }
    return { 'Content-Type': 'application/json', 'X-Api-Key': userApiKey, 'Authorization': id_token };
};

interface TenderSummary {
    id: string;
    name: string;
    matchingScore: number;
}

const localApiService = {
    fetchTenders: async (auth: any, group?: string): Promise<TenderSummary[]> => {
        const headers: Record<string, string> = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        // /tenders is paginated ({items, cursor}) — follow the cursor;
        // a plain array (pre-pagination lambda) is accepted for deploy skew.
        type TenderRow = { TenderId: string; Name?: string; MatchingScore: number };
        const all: TenderRow[] = [];
        let cursor: string | null = null;
        do {
            const params = new URLSearchParams();
            if (group) params.set('company', group);
            if (cursor) params.set('cursor', cursor);
            const qs = params.toString();
            const res = await fetch(`${baseUrl}/tenders${qs ? `?${qs}` : ''}`, { headers });
            if (!res.ok) throw new Error((await res.json()).message || 'Failed to fetch tenders');
            const data: TenderRow[] | { items: TenderRow[]; cursor: string | null } = await res.json();
            if (Array.isArray(data)) {
                all.push(...data);
                cursor = null;
            } else {
                all.push(...(data.items ?? []));
                cursor = data.cursor ?? null;
            }
        } while (cursor);
        return all.map(t => ({
            id: t.TenderId,
            name: t.Name || 'Unnamed Tender',
            matchingScore: t.MatchingScore,
        }));
    },
    createProposal: async (tenderId: string, auth: any, outputLanguage: string = 'en', customInstructions: string = '') => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const res = await fetch(`${baseUrl}/proposals`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                tenderId,
                cognitoGroups: auth.user?.profile?.["cognito:groups"],
                outputLanguage,
                customInstructions,
            })
        });
        if (!res.ok) throw new Error((await res.json()).message);
        return res.json();
    },
    getWorkbench: async (proposalId: string, auth: any): Promise<WorkbenchResponse> => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const res = await fetch(`${baseUrl}/proposals/${proposalId}/workbench`, { headers });
        if (!res.ok) throw new Error((await res.json()).message);
        return res.json();
    },
    updateItem: async (proposalId: string, itemId: string, content: string, auth: any) => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        // Encode the SortKey (Item ID) to handle '#' characters correctly
        const encodedItemId = encodeURIComponent(itemId);
        const res = await fetch(`${baseUrl}/proposals/${proposalId}/items/${encodedItemId}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ content })
        });
        if (!res.ok) throw new Error((await res.json()).message);
        return res.json();
    },
    regenerateItem: async (proposalId: string, itemId: string, feedback: string, auth: any): Promise<{ content: string; previous_content: string }> => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const encodedItemId = encodeURIComponent(itemId);
        const res = await fetch(`${baseUrl}/proposals/${proposalId}/items/${encodedItemId}/regenerate`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ feedback })
        });
        if (!res.ok) {
            const errText = await res.text();
            try {
                throw new Error(JSON.parse(errText).message || errText);
            } catch {
                throw new Error(errText || 'Regeneration failed');
            }
        }
        return res.json();
    },
    approveStage: async (proposalId: string, auth: any) => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const res = await fetch(`${baseUrl}/proposals/${proposalId}/stage/approve`, { method: 'POST', headers });
        if (!res.ok) throw new Error((await res.json()).message);
        return res.json();
    },
    getArtifactUrl: async (proposalId: string, format: 'pdf' | 'docx', auth: any) => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        
        const url = `${baseUrl}/proposals/${proposalId}/artifacts?format=${format}`;
        
        console.log(`%c[Frontend] Requesting Download:`, 'color: blue; font-weight: bold');
        console.log(`URL: ${url}`);
        console.log(`Payload (Params):`, { proposalId, format });

        const res = await fetch(url, { headers });
        
        if (!res.ok) {
            const errorText = await res.text();
            console.error("[Frontend] Error response:", errorText);
            throw new Error(errorText || 'Failed to get download URL');
        }

        const data = await res.json();

        console.log(`%c[Frontend] Backend Response:`, 'color: green; font-weight: bold', data);
        
        return data;
    }
};

// SortKey patterns where regeneration is a free-text LLM call. The two
// structured Phase 3 sections (04_TEAM, 06_REFERENCES) are generated by
// separate selection pipelines that build HTML from JSON — we can't
// regenerate those from this endpoint without losing that structure.
const isRegenerable = (sortKey: string): boolean => {
    if (sortKey.includes('PHASE_2')) return true;
    if (sortKey.includes('PHASE_3')) {
        return !sortKey.endsWith('04_TEAM') && !sortKey.endsWith('06_REFERENCES');
    }
    return false;
};

// --- SUB-COMPONENT: Editable Item Card ---
const ItemCard: React.FC<{ item: ProposalItem; proposalId: string; auth: any }> = ({ item, proposalId, auth }) => {
    const [content, setContent] = useState(item.content);
    const [isDirty, setIsDirty] = useState(false);
    const [showRegen, setShowRegen] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [previousContent, setPreviousContent] = useState<string | null>(null);
    const [regenError, setRegenError] = useState<string | null>(null);

    const updateMutation = useMutation({
        mutationFn: () => localApiService.updateItem(proposalId, item.SortKey, content, auth),
        onSuccess: () => setIsDirty(false)
    });

    const regenMutation = useMutation({
        mutationFn: () => localApiService.regenerateItem(proposalId, item.SortKey, feedback, auth),
        onSuccess: (data) => {
            setPreviousContent(data.previous_content ?? content);
            setContent(data.content);
            setIsDirty(false);
            setShowRegen(false);
            setFeedback('');
            setRegenError(null);
        },
        onError: (err: Error) => {
            setRegenError(err.message || 'Regeneration failed');
        }
    });

    const undoMutation = useMutation({
        mutationFn: () => localApiService.updateItem(proposalId, item.SortKey, previousContent || '', auth),
        onSuccess: () => {
            if (previousContent !== null) setContent(previousContent);
            setPreviousContent(null);
            setIsDirty(false);
        }
    });

    const handleContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setContent(e.target.value);
        setIsDirty(true);
    };

    const canRegenerate = isRegenerable(item.SortKey);
    const isRegenerating = regenMutation.isPending;

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                mb: 2,
                bgcolor: 'white',
                borderColor: isDirty ? 'warning.light' : (isRegenerating ? '#9c27b0' : 'divider'),
                borderWidth: isDirty || isRegenerating ? 2 : 1,
                position: 'relative',
                transition: 'border-color 200ms ease, box-shadow 200ms ease',
                boxShadow: isRegenerating ? '0 0 0 3px rgba(156, 39, 176, 0.12)' : undefined,
                overflow: 'hidden',
            }}
        >
            {/* Animated top bar while regenerating */}
            {isRegenerating && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, height: 3,
                        background: 'linear-gradient(90deg, #9c27b0, #2196f3, #9c27b0)',
                        backgroundSize: '200% 100%',
                        animation: 'qabotRegenShimmer 1.6s linear infinite',
                        '@keyframes qabotRegenShimmer': {
                            '0%':   { backgroundPosition: '200% 0' },
                            '100%': { backgroundPosition: '-200% 0' },
                        },
                    }}
                />
            )}

            {item.related_question && (
                <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, borderLeft: '4px solid #1976d2' }}>
                    <Typography variant="caption" color="primary" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
                        Requirement / Question
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {item.related_question}
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Chip label={item.type || "ITEM"} size="small" color="default" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                <Stack direction="row" spacing={1} alignItems="center">
                    {item.is_user_edited && !isDirty && !previousContent && (
                        <Chip label="Edited" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    )}
                    {previousContent !== null && !isDirty && (
                        <Chip
                            icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                            label="Regenerated"
                            size="small"
                            sx={{ fontSize: '0.7rem', bgcolor: '#f3e5f5', color: '#6a1b9a' }}
                        />
                    )}
                    {isDirty && <Chip label="Unsaved Changes" size="small" color="warning" sx={{ fontSize: '0.7rem' }} />}
                </Stack>
            </Box>

            <TextField
                fullWidth
                multiline
                label={item.related_question ? "Draft Answer" : "Content"}
                minRows={2}
                value={content}
                onChange={handleContentChange}
                disabled={isRegenerating}
                variant="outlined"
                sx={{
                    mb: 1,
                    bgcolor: item.related_question ? '#fff' : 'inherit',
                    opacity: isRegenerating ? 0.55 : 1,
                    transition: 'opacity 200ms ease',
                }}
            />

            {/* Action row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    {canRegenerate && !showRegen && !isRegenerating && (
                        <Tooltip title="Ask the AI to rewrite this with your feedback">
                            <Button
                                startIcon={<AutoAwesomeIcon />}
                                onClick={() => { setShowRegen(true); setRegenError(null); }}
                                size="small"
                                variant="outlined"
                                sx={{
                                    borderColor: '#9c27b0',
                                    color: '#6a1b9a',
                                    '&:hover': { borderColor: '#6a1b9a', bgcolor: 'rgba(156, 39, 176, 0.04)' },
                                    textTransform: 'none',
                                }}
                            >
                                Regenerate with AI
                            </Button>
                        </Tooltip>
                    )}
                    {previousContent !== null && !isRegenerating && (
                        <Tooltip title="Restore the version from before the last regeneration">
                            <span>
                                <Button
                                    startIcon={<UndoIcon />}
                                    onClick={() => undoMutation.mutate()}
                                    disabled={undoMutation.isPending}
                                    size="small"
                                    variant="text"
                                    color="inherit"
                                    sx={{ textTransform: 'none' }}
                                >
                                    {undoMutation.isPending ? 'Restoring...' : 'Undo regeneration'}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Stack>

                {isDirty && (
                    <Button
                        startIcon={<SaveIcon />}
                        disabled={updateMutation.isPending || isRegenerating}
                        onClick={() => updateMutation.mutate()}
                        size="small"
                        variant="contained"
                        color="warning"
                    >
                        {updateMutation.isPending ? "Saving..." : "Save Changes"}
                    </Button>
                )}
            </Box>

            {/* Regeneration feedback panel */}
            <Collapse in={showRegen} timeout={250} unmountOnExit>
                <Box
                    sx={{
                        mt: 2, p: 2, borderRadius: 1,
                        background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.06), rgba(33, 150, 243, 0.06))',
                        border: '1px solid rgba(156, 39, 176, 0.25)',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#6a1b9a', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            <AutoAwesomeIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                            What should change?
                        </Typography>
                        <IconButton size="small" onClick={() => { setShowRegen(false); setFeedback(''); setRegenError(null); }} aria-label="Cancel regeneration">
                            <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        autoFocus
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value.slice(0, 2000))}
                        placeholder={
                            "e.g. Make this more concise. Mention our ISO 27001 certification. " +
                            "Tone down the marketing language. Add a concrete example from the BernCloud project."
                        }
                        helperText={`${feedback.length}/2000 — leave empty for a free rewrite`}
                        variant="outlined"
                        size="small"
                        sx={{ bgcolor: 'white' }}
                    />
                    {regenError && <Alert severity="error" sx={{ mt: 1 }}>{regenError}</Alert>}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                        <Button
                            size="small"
                            onClick={() => { setShowRegen(false); setFeedback(''); setRegenError(null); }}
                            sx={{ textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => regenMutation.mutate()}
                            disabled={regenMutation.isPending}
                            sx={{
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #9c27b0, #2196f3)',
                                boxShadow: 'none',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #7b1fa2, #1976d2)',
                                    boxShadow: '0 2px 8px rgba(156, 39, 176, 0.35)',
                                },
                            }}
                        >
                            {regenMutation.isPending ? 'Regenerating...' : 'Regenerate'}
                        </Button>
                    </Box>
                </Box>
            </Collapse>

            {/* In-place loading overlay */}
            <Fade in={isRegenerating} timeout={200} unmountOnExit>
                <Box
                    sx={{
                        mt: 1.5, px: 2, py: 1.5, borderRadius: 1,
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        bgcolor: 'rgba(156, 39, 176, 0.06)',
                        border: '1px dashed rgba(156, 39, 176, 0.35)',
                    }}
                >
                    <CircularProgress size={18} sx={{ color: '#9c27b0' }} />
                    <Typography variant="body2" sx={{ color: '#6a1b9a' }}>
                        AI is rewriting this section with your feedback...
                    </Typography>
                </Box>
            </Fade>
        </Paper>
    );
};

// --- MAIN PAGE COMPONENT ---
const ProposalDraftPage: React.FC = () => {
    const { tenderId } = useParams<{ tenderId: string }>();
    const navigate = useNavigate();
    const auth = useAuth();
    const queryClient = useQueryClient();
    const [activeStep, setActiveStep] = useState(0);
    const [proposalId, setProposalId] = useState<string | null>(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [outputLanguage, setOutputLanguage] = useState<string>('en');
    const [customInstructions, setCustomInstructions] = useState<string>('');
    const CUSTOM_INSTRUCTIONS_MAX = 4000;

    const userPrimaryGroup = (auth.user?.profile?.["cognito:groups"] as string[] | undefined)?.[0];
    const isAuthReady = auth.isAuthenticated && !!auth.user?.profile?.['custom:api_key'];

    // Shares the TenderQPage cache when user navigated from there; otherwise fetches once.
    const tendersQuery = useQuery({
        queryKey: ['tenders', userPrimaryGroup],
        queryFn: () => localApiService.fetchTenders(auth, userPrimaryGroup),
        enabled: isAuthReady,
    });
    const tender = (tendersQuery.data as TenderSummary[] | undefined)?.find(t => t.id === tenderId);
    const scorePercent = tender
        ? (tender.matchingScore <= 1 ? Math.round(tender.matchingScore * 100) : Math.round(tender.matchingScore))
        : 0;
    const scoreColor: 'success' | 'warning' | 'error' = scorePercent >= 70 ? 'success' : scorePercent >= 40 ? 'warning' : 'error';
    const scoreLabel = scorePercent >= 70 ? 'Good Fit' : scorePercent >= 40 ? 'Moderate Fit' : 'Low Fit';

    // Steps for the Stepper UI
    const steps = ['Initialize', 'Extract Requirements', 'Review Requirements', 'Draft Answers', 'Review Answers', 'Finalize Proposal', 'Download'];

    // START WORKFLOW
    const createProposalMutation = useMutation({
        mutationFn: () => localApiService.createProposal(tenderId!, auth, outputLanguage, customInstructions.trim()),
        onSuccess: (data) => {
            setProposalId(data.proposalId);
            setActiveStep(1); // Move to "Extracting"
        }
    });

    // POLL STATUS (Runs whenever we have an ID)
    const workbenchQuery = useQuery({
        queryKey: ['workbench', proposalId],
        queryFn: () => localApiService.getWorkbench(proposalId!, auth),
        enabled: !!proposalId,
        refetchInterval: (query) => {
            const phase = query.state.data?.phase;
            
            // If we are manually transitioning, ALWAYS poll (ignore the phase check)
            if (isTransitioning) return 1000; // Fast poll (1s) to catch the update quickly

            // Normal logic: Stop polling if waiting for user, keep polling if processing
            if (phase === 'PHASE_1' || phase === 'PHASE_2' || phase === 'DONE') {
                return false; 
            }
            return 2000; 
        },
    });

    // APPROVE PHASE
    const approveMutation = useMutation({
        mutationFn: () => localApiService.approveStage(proposalId!, auth),
        onMutate: () => {
            // TURN ON "Blind Mode" - Ignore server data for a moment
            setIsTransitioning(true);
        },
        onSuccess: () => {
            // Force a refetch immediately
            workbenchQuery.refetch(); 
        },
        onError: () => {
            // If API fails, turn off the override so the user sees the error/form again
            setIsTransitioning(false);
        }
    });

    const handleDownload = async (format: 'pdf' | 'docx') => {
        if (!proposalId) return;
        setIsDownloading(true);
        try {
            const data = await localApiService.getArtifactUrl(proposalId, format, auth);
            
            if (data.downloadUrl) {
                // Open the Presigned URL in a new tab
                window.open(data.downloadUrl, '_blank');
            } else {
                alert("Error: No URL returned from backend.");
            }
        } catch (error) {
            console.error(error);
            alert("Failed to download file. Check console for details.");
        } finally {
            setIsDownloading(false);
        }
    };

    // --- STATE LOGIC ---
    const phase = workbenchQuery.data?.phase;
    const items = workbenchQuery.data?.items || [];

    // Sync Stepper with Phase
    useEffect(() => {
        if (!phase) return;

        if (phase === 'PHASE_1') {
            setActiveStep(2);
            // If we were transitioning, but the DB says Phase 1, we are likely still waiting.
            // BUT: If we just clicked approve (isTransitioning=true), we assume this is STALE data.
            // So we DO NOT set isTransitioning(false) here. We wait for the change.
        }
        else if (phase === 'PHASE_2') {
            setActiveStep(4);
            setIsTransitioning(false); // <--- We arrived at Phase 2! Stop forcing load.
        }
        else if (phase === 'PHASE_3') {
            setActiveStep(5);
            setIsTransitioning(false); // <--- We arrived at Phase 3!
        }
        else if (phase === 'DONE') {
            setActiveStep(6);
            setIsTransitioning(false); // <--- We are Done!
        }
        else if (phase === 'STARTING') {
            setActiveStep(1);
        }
    }, [phase]);

    // --- RENDER HELPERS ---

    const renderLoadingState = (message: string) => (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
            <Typography variant="h6" color="text.secondary">{message}</Typography>
            <Typography variant="body2" color="text.disabled">AI Agents are working...</Typography>
        </Box>
    );

    const renderEditor = (title: string, description: string, buttonText: string) => (
        <Box>
            <Paper sx={{ p: 3, mb: 3, bgcolor: '#e3f2fd', borderLeft: '6px solid #2196f3' }}>
                <Typography variant="h6" gutterBottom>{title}</Typography>
                <Typography variant="body2">{description}</Typography>
            </Paper>

            {items.map((item) => (
                <ItemCard key={item.SortKey} item={item} proposalId={proposalId!} auth={auth} />
            ))}

            <Box sx={{ mt: 4, pb: 4, display: 'flex', justifyContent: 'center' }}>
                <Button 
                    variant="contained" 
                    size="large" 
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => approveMutation.mutate()}
                    disabled={approveMutation.isPending}
                    sx={{ px: 6, py: 1.5, borderRadius: 2 }}
                >
                    {approveMutation.isPending ? "Processing..." : buttonText}
                </Button>
            </Box>
        </Box>
    );

    const renderSuccessState = () => (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" gutterBottom>Proposal Completed!</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                The AI has successfully assembled your proposal document based on the extracted requirements and drafted answers.
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center">
                <Button 
                    variant="contained" 
                    size="large" 
                    startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                    onClick={() => handleDownload('pdf')}
                    disabled={isDownloading}
                >
                    Download PDF
                </Button>
                <Button 
                    variant="outlined" 
                    size="large" 
                    startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DescriptionIcon />}
                    onClick={() => handleDownload('docx')}
                    disabled={isDownloading}
                >
                    Download Word
                </Button>
            </Stack>
        </Box>
    );

    // --- MAIN RENDER ---
    return (
        <Box sx={{ 
            bgcolor: '#f8f9fa', 
            height: '100vh', 
            display: 'flex', 
            flexDirection: 'column', 
            overflow: 'hidden' 
        }}>
            {/* Fixed Header */}
            <Box sx={{ 
                p: 2, 
                bgcolor: 'white', 
                borderBottom: '1px solid #ddd', 
                flexShrink: 0, 
                display: 'flex',
                alignItems: 'center'
            }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tenderq')}>
                    Back to Tenders
                </Button>
            </Box>

            {/* Scrollable Content Area */}
            <Box sx={{ 
                flexGrow: 1, 
                overflowY: 'auto', 
                p: 3 
            }}>
                <Container maxWidth="md">
                    <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                            Proposal Generator Agent
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="h5" fontWeight="bold">
                                {tender?.name ?? (tendersQuery.isLoading ? 'Loading tender...' : 'Unnamed Tender')}
                            </Typography>
                            {tender && (
                                <Chip
                                    label={`${scoreLabel} · ${scorePercent}%`}
                                    color={scoreColor}
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                />
                            )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 4 }}>
                            Tender ID: {tenderId}
                        </Typography>

                        <Stepper activeStep={activeStep} alternativeLabel>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Paper>

                    {/* CONTENT AREA */}
                    <Paper elevation={1} sx={{ p: 4, minHeight: '400px', mb: 8 }}>
                        
                        {/* NOT STARTED */}
                        {!proposalId && (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography variant="h6" gutterBottom>Ready to start?</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    This will initialize the AI Agents to read the tender documents.
                                </Typography>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Proposal Output Language
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={outputLanguage}
                                        exclusive
                                        onChange={(_, val) => { if (val) setOutputLanguage(val); }}
                                        size="small"
                                    >
                                        <ToggleButton value="en">English</ToggleButton>
                                        <ToggleButton value="de">Deutsch</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>

                                <Box sx={{ mb: 4, textAlign: 'left', maxWidth: 640, mx: 'auto' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Custom Instructions (optional)
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        maxRows={10}
                                        value={customInstructions}
                                        onChange={(e) => setCustomInstructions(e.target.value.slice(0, CUSTOM_INSTRUCTIONS_MAX))}
                                        placeholder={
                                            "Notes for the proposal writer. For example:\n" +
                                            "• Tone: confident but not boastful; avoid marketing fluff.\n" +
                                            "• We find the data-migration scope especially interesting — make that apparent.\n" +
                                            "• Don't forget to mention our ISO 27001 certification in the conclusion."
                                        }
                                        helperText={
                                            `Applied as soft guidance where naturally relevant (win strategy, drafting, coherence pass, narrative sections). Not used for requirements extraction or team/reference selection. ${customInstructions.length}/${CUSTOM_INSTRUCTIONS_MAX}`
                                        }
                                        variant="outlined"
                                    />
                                </Box>

                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<PlayArrowIcon />}
                                    onClick={() => createProposalMutation.mutate()}
                                    disabled={createProposalMutation.isPending}
                                >
                                    {createProposalMutation.isPending ? "Initializing..." : "Start Workflow"}
                                </Button>
                                {createProposalMutation.isError && (
                                    <Alert severity="error" sx={{ mt: 3 }}>{createProposalMutation.error.message}</Alert>
                                )}
                            </Box>
                        )}

                        {/* LOADING / PROCESSING STATES */}
                        {proposalId && (isTransitioning || approveMutation.isPending || phase === 'STARTING' || phase === 'PROCESSING' || !phase) && (
                            renderLoadingState(
                                activeStep >= 4 ? "Finalizing Proposal..." : 
                                activeStep >= 2 ? "Drafting Answers..." : 
                                "Extracting Requirements..."
                            )
                        )}

                        {/* PHASE 1: REVIEW REQUIREMENTS */}
                        {proposalId && phase === 'PHASE_1' && !isTransitioning && !approveMutation.isPending && (
                            renderEditor(
                                "Review Requirements", 
                                "The AI has analyzed the tender documents and extracted the following requirements. Please review, add, or edit them to ensure we answer the right questions.",
                                "Approve & Start Drafting" 
                            )
                        )}

                        {/* PHASE 2: REVIEW ANSWERS */}
                        {proposalId && phase === 'PHASE_2' && !isTransitioning && !approveMutation.isPending && (
                            renderEditor(
                                "Review Draft Answers", 
                                "The AI has generated draft answers for each requirement using your company's knowledge base. Polish these answers now. Once approved, the AI will generate the Executive Summary and final structure.",
                                "Approve & Generate Proposal"
                            )
                        )}

                        {/* PHASE 3: REVIEW FULL PROPOSAL */}
                        {proposalId && phase === 'PHASE_3' && !isTransitioning && !approveMutation.isPending && (
                            renderEditor(
                                "Finalize Proposal", 
                                "The AI has generated the Executive Summary and Conclusion. Review these final sections to ensure the tone matches your company voice.",
                                "Approve & Generate PDF"
                            )
                        )}

                        {/* DONE */}
                        {proposalId && phase === 'DONE' && !isTransitioning && (
                            renderSuccessState()
                        )}

                    </Paper>
                </Container>
            </Box>
        </Box>
    );
};

export default ProposalDraftPage;